import React, { ReactNode, useEffect, useReducer, useState } from 'react';
import cn from 'classnames';
import { LoaderIcon } from '@repo/icons';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import ConnectionsPanel from './panels/ConnectionsPanel';
import ProfilePanel from './panels/ProfilePanel';
import styles from './Onboarding.module.scss';
import OnboardingNotificationArea from './OnboardingNotification';
import { apiRequest } from '@repo/utils/helpers/api';
import { useAuthContext } from '@repo/hooks';
import PublicProfilePanel from '@views/Onboarding/panels/PublicProfilePanel/PublicProfilePanel';
import SelectAccountTypePanel, { AccountType } from './panels/SelectAccountTypePanel';
import WorkspacePanel from './panels/WorkspacePanel';
import AddWorkspaceMembersPanel from './panels/AddWorkspaceMembersPanel';
import { useNavigate } from 'react-router-dom';
import PaymentPanel, { ClayProduct, SetupIntent } from './panels/PaymentPanel';
import { OrderedMap } from 'immutable';
import StripeProvider from '@app/Context/StripeProvider';
import OnboardingLayout from './OnboardingLayout';
import { getStripeTestModeEnabled } from '@views/Settings/panels/Staging/StripeTesting';
import { useStripe } from '@stripe/react-stripe-js';
import { supportEmailProps } from '@repo/utils/helpers/preferred-email-client';
import { getStoredSelectedAccountType } from './panels/PaymentPanel/PaymentForm';
import HowDidYouHearPanel, { ClayUserReference } from './panels/HowDidYouHearPanel';
import InviteFriendsPanel from './panels/InviteFriendsPanel';
import SubscribePanel from './panels/Subscribe';
import { IAPProduct } from '@repo/utils/types';

enum OnboardingPanel {
  selectAccountType = 'selectAccountType',
  payment = 'payment',
  howDidYouHear = 'howDidYouHear',
  connections = 'connections',
  profile = 'profile',
  inviteFriends = 'inviteFriends',
  publicProfile = 'publicProfile',
  workspace = 'workspace',
  addWorkspaceMembers = 'addWorkspaceMembers',
  subscribe = 'subscribe',
}

interface OnboardingState {
  needsPaymentInfo: boolean;
  loadingNeedsPaymentInfo: boolean;
  selectedProduct: AccountType;
  renderedPanel: OnboardingPanel;
  productsMap: OrderedMap<AccountType, ClayProduct>;
  loadingProductDetails: boolean;
  loadingMessage?: ReactNode;
  setupIntent?: SetupIntent;
  setupIntentLoaded?: boolean;
}

const defaultOnboardingState: OnboardingState = {
  needsPaymentInfo: true,
  loadingNeedsPaymentInfo: true,
  selectedProduct: undefined,
  renderedPanel: undefined,
  productsMap: OrderedMap<AccountType, ClayProduct>(),
  loadingProductDetails: false,
  loadingMessage: undefined,
  setupIntent: undefined,
  setupIntentLoaded: false,
};

type OnboardingAction =
  | ['goToPanel', OnboardingPanel]
  | ['selectAccountType', AccountType]
  | ['setNeedsPaymentInfo', boolean, AccountType, SetupIntent]
  | ['setLoadingProductDetails', boolean]
  | ['setProductDetailsAndGoToForm', ClayProduct]
  | ['setLoadingMessage', ReactNode];

const onboardingReducer = (state: OnboardingState, action: OnboardingAction): OnboardingState => {
  switch (action[0]) {
    case 'goToPanel':
      return {
        ...state,
        renderedPanel: action[1],
      };

    case 'selectAccountType':
      return {
        ...state,
        selectedProduct: action[1],
      };

    // if the user needs payment info,
    //   - use loaded setup intent, if it exists.
    //   - mark setup intent as loaded.
    case 'setNeedsPaymentInfo':
      const [_, needsPaymentInfo, selectedProduct, setupIntent] = action;

      return {
        ...state,
        needsPaymentInfo,
        loadingNeedsPaymentInfo: false,
        ...(needsPaymentInfo
          ? {
              setupIntent,
              setupIntentLoaded: true,
            }
          : {
              selectedProduct,
            }),
      };

    case 'setProductDetailsAndGoToForm':
      const product = new ClayProduct(action[1]);
      return {
        ...state,
        productsMap: state.productsMap.set(product.name, product),
        renderedPanel: OnboardingPanel.payment,
        loadingProductDetails: false,
      };

    case 'setLoadingProductDetails':
      return {
        ...state,
        loadingProductDetails: action[1],
      };

    case 'setLoadingMessage':
      return {
        ...state,
        loadingMessage: action[1],
      };

    default:
      return state;
  }
};

function Onboarding() {
  const navigate = useNavigate();
  const stripe = useStripe();
  const { getIsTeamsPlan } = useAuthContext();
  const { getEntitlementValue } = useAuthContext();
  const [
    {
      renderedPanel,
      selectedProduct,
      productsMap,
      loadingProductDetails,
      loadingMessage,
      setupIntent,
      setupIntentLoaded,
    },
    dispatchToOnboarding,
  ] = useReducer(onboardingReducer, defaultOnboardingState);
  const [clayUserReferences, setClayUserReferences] = useState<ClayUserReference[]>([]);
  const [iAPProducts, setIAPProducts] = useState<IAPProduct[]>([]);
  const [showImportingContactsAlert, setShowImportingContactsAlert] = useState(false);

  const supportMsg = (
    <span>
      Something went wrong, please&nbsp;
      <a {...supportEmailProps({ subject: 'Error in app onboarding' })}>contact support</a>.
    </span>
  );

  const fetchProductDetails = (product: AccountType) =>
    apiRequest<any>('GET', `/billing/details/${product}`, undefined, { apiVersion: 'v2' });

  const goToPaymentPanel = async (product?: AccountType) => {
    const _product = selectedProduct || product;

    if (!productsMap.get(_product)) {
      dispatchToOnboarding(['setLoadingProductDetails', true]);

      try {
        const productDetails = await fetchProductDetails(_product);
        dispatchToOnboarding(['setProductDetailsAndGoToForm', productDetails]);
        return;
      } catch (e) {
        console.error(e);
        dispatchToOnboarding(['setLoadingMessage', supportMsg]);
        dispatchToOnboarding(['setLoadingProductDetails', false]);
        return;
      }
    } else {
      dispatchToOnboarding(['goToPanel', OnboardingPanel.payment]);
    }
  };

  const checkIfPaymentInfoNeeded = async () => {
    const useTestMode = getStripeTestModeEnabled();
    let needsPaymentInfo = undefined;
    let storedSetupIntent = undefined;
    let selectedPlan = undefined;

    try {
      const resp = await apiRequest<any>(
        'GET',
        `/billing/status/`,
        { query: useTestMode ? { dev: 1 } : {} },
        { apiVersion: 'v2' },
      );
      needsPaymentInfo = resp.needs_payment_info;
    } catch (e) {
      console.error(e);
      needsPaymentInfo = getIsTeamsPlan() ? false : true;
    }

    if (needsPaymentInfo) {
      try {
        storedSetupIntent = await fetchStripeSetupIntent();
      } catch (e) {
        console.error(e);
      }
    }

    if (!needsPaymentInfo) {
      const storedSelectedAccountType = getStoredSelectedAccountType();
      if (storedSelectedAccountType) {
        selectedPlan = storedSelectedAccountType;
      } else {
        selectedPlan = getIsTeamsPlan() ? 'teams' : 'pro';
      }
    }

    dispatchToOnboarding([
      'setNeedsPaymentInfo',
      needsPaymentInfo,
      selectedPlan,
      storedSetupIntent,
    ]);

    return needsPaymentInfo;
  };

  const fetchStripeSetupIntent = async () => {
    const useTestMode = getStripeTestModeEnabled();
    return await apiRequest<SetupIntent>(
      'GET',
      '/billing/stripe/payment/',
      { query: useTestMode ? { dev: 1 } : {} },
      {
        apiVersion: 'v2',
      },
    );
  };

  const fetchMethodsOfLearningAboutClay = async () => {
    try {
      const userReferences = await apiRequest<ClayUserReference[]>(
        'GET',
        '/onboarding/references/',
        undefined,
        { apiVersion: 'v2' },
      );
      setClayUserReferences(userReferences);
      return userReferences;
    } catch (e) {
      console.error('Failed to load api/v2/onboarding/references/', e);
      return [];
    }
  };

  const fetchAlreadySelectedClayUserReference = async () => {
    try {
      const previousUserSelection = await apiRequest<{ reference?: string }>(
        'GET',
        '/onboarding/reference/',
        undefined,
        { apiVersion: 'v2' },
      );

      return !!previousUserSelection?.reference;
    } catch (e) {
      console.error('Failed to load api/v2/onboarding/reference/', e);
      return true;
    }
  };

  const finishInAppPurchaseFlow = () => {
    dispatchToOnboarding(['setNeedsPaymentInfo', false, 'pro', undefined]);
    dispatchToOnboarding(['goToPanel', OnboardingPanel.connections]);
  };

  const determineFirstPrePaymentPanel = async () => {
    if (!stripe || renderedPanel || !setupIntentLoaded) {
      return;
    }

    if (setupIntentLoaded && !setupIntent) {
      dispatchToOnboarding(['setLoadingMessage', supportMsg]);
      return;
    }

    if (!setupIntent?.client_secret) {
      return;
    }

    const methodsOfHearingAboutClay = await fetchMethodsOfLearningAboutClay();
    const alreadySelectedClayUserReference = await fetchAlreadySelectedClayUserReference();
    const { setupIntent: stripeSetupIntent } = await stripe.retrieveSetupIntent(
      setupIntent?.client_secret,
    );

    switch (stripeSetupIntent.status) {
      case 'succeeded':
      case 'requires_payment_method':
        if (alreadySelectedClayUserReference || methodsOfHearingAboutClay.length === 0) {
          dispatchToOnboarding(['goToPanel', OnboardingPanel.selectAccountType]);
        } else {
          dispatchToOnboarding(['goToPanel', OnboardingPanel.howDidYouHear]);
        }
        break;
      case 'processing':
        dispatchToOnboarding(['setLoadingMessage', 'Your payment is processing.']);
        break;
      default:
        dispatchToOnboarding(['setLoadingMessage', supportMsg]);
        break;
    }
  };

  const determineFirstPrePaymentMASPanel = async () => {
    const methodsOfHearingAboutClay = await fetchMethodsOfLearningAboutClay();
    const alreadySelectedClayUserReference = await fetchAlreadySelectedClayUserReference();
    dispatchToOnboarding(['selectAccountType', 'pro']);

    if (alreadySelectedClayUserReference || methodsOfHearingAboutClay.length === 0) {
      goToPaymentPanel('pro');
    } else {
      dispatchToOnboarding(['goToPanel', OnboardingPanel.howDidYouHear]);
    }
  };

  const determineFirstPanel = async () => {
    const needsPaymentInfo = await checkIfPaymentInfoNeeded();

    if (!needsPaymentInfo) {
      return dispatchToOnboarding(['goToPanel', OnboardingPanel.connections]);
    }

    if (window.desktopIntegrations?.appInfo.isMAS) {
      window.desktopIntegrations.iap
        .getProducts()
        .then((products) => {
          setIAPProducts(products);
        })
        .finally(() => {
          determineFirstPrePaymentMASPanel();
        });
    }
  };

  useEffect(() => {
    determineFirstPanel();
  }, []);

  useEffect(() => {
    if (
      !renderedPanel &&
      stripe &&
      setupIntentLoaded &&
      !window.desktopIntegrations?.appInfo.isMAS
    ) {
      determineFirstPrePaymentPanel();
    }
  }, [stripe, setupIntentLoaded]);

  return (
    <OnboardingLayout
      // back button now only shows on payment panel (except for MAS, which doesn't include account type selection)
      onBackBtnClick={() => dispatchToOnboarding(['goToPanel', OnboardingPanel.selectAccountType])}
      hideBackBtn={
        window.desktopIntegrations?.appInfo.isMAS || renderedPanel !== OnboardingPanel.payment
      }
      showImportingContactsAlert={showImportingContactsAlert}
    >
      <OnboardingNotificationArea />
      <StateBasedFadeTransition
        state={renderedPanel || 'loading'}
        elementByStateMap={{
          [OnboardingPanel.howDidYouHear]: (
            <HowDidYouHearPanel
              clayUserReferences={clayUserReferences}
              proceedToNextStep={() => {
                if (window.desktopIntegrations?.appInfo.isMAS) {
                  goToPaymentPanel('pro');
                } else {
                  dispatchToOnboarding(['goToPanel', OnboardingPanel.selectAccountType]);
                }
              }}
            />
          ),
          [OnboardingPanel.selectAccountType]: (
            <SelectAccountTypePanel
              proceedToNextStep={goToPaymentPanel}
              accountTypePreference={selectedProduct}
              setAccountTypePreference={(accountType: AccountType) =>
                dispatchToOnboarding(['selectAccountType', accountType])
              }
              loadingProductDetails={loadingProductDetails}
            />
          ),
          [OnboardingPanel.payment]: (
            <PaymentPanel
              product={
                window.desktopIntegrations?.appInfo.isMAS
                  ? productsMap.get('pro')
                  : productsMap.get(selectedProduct)
              }
              setupIntent={setupIntent}
              iAPProducts={iAPProducts}
              onProductPurchased={finishInAppPurchaseFlow}
            />
          ),
          [OnboardingPanel.connections]: (
            <ConnectionsPanel
              proceedToNextStep={() => dispatchToOnboarding(['goToPanel', OnboardingPanel.profile])}
              setShowImportingContactsAlert={setShowImportingContactsAlert}
            />
          ),
          [OnboardingPanel.profile]: (
            <ProfilePanel
              proceedToNextStep={() =>
                dispatchToOnboarding(['goToPanel', OnboardingPanel.publicProfile])
              }
            />
          ),
          [OnboardingPanel.publicProfile]: (
            <PublicProfilePanel
              proceedToNextStep={() => {
                if (
                  selectedProduct === 'pro' ||
                  (window.desktopIntegrations?.appInfo.isMAS &&
                    !getEntitlementValue('account.team_member'))
                ) {
                  dispatchToOnboarding(['goToPanel', OnboardingPanel.inviteFriends]);
                } else if (getEntitlementValue('account.team_member')) {
                  dispatchToOnboarding(['goToPanel', OnboardingPanel.subscribe]);
                } else {
                  dispatchToOnboarding(['goToPanel', OnboardingPanel.workspace]);
                }
              }}
            />
          ),
          [OnboardingPanel.workspace]: (
            <WorkspacePanel
              proceedToNextStep={() =>
                dispatchToOnboarding(['goToPanel', OnboardingPanel.addWorkspaceMembers])
              }
            />
          ),
          [OnboardingPanel.addWorkspaceMembers]: (
            <AddWorkspaceMembersPanel
              proceedToNextStep={() =>
                dispatchToOnboarding(['goToPanel', OnboardingPanel.subscribe])
              }
            />
          ),
          [OnboardingPanel.inviteFriends]: (
            <InviteFriendsPanel
              proceedToNextStep={() =>
                dispatchToOnboarding(['goToPanel', OnboardingPanel.subscribe])
              }
            />
          ),
          [OnboardingPanel.subscribe]: (
            <SubscribePanel proceedToNextStep={() => navigate('/tour')} />
          ),
          loading: (
            <div className={cn(styles.loader, !loadingMessage && styles.noMessage)}>
              <div className={styles.loadingMessage}></div>
              <LoaderIcon />
            </div>
          ),
        }}
      />
    </OnboardingLayout>
  );
}

export default function WrappedOnboarding() {
  return (
    <StripeProvider>
      <Onboarding />
    </StripeProvider>
  );
}
